import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../components/ui/Button";
import { useSiteContent } from "../lib/content";
import { SERVICE_ICONS, type SiteContent } from "../lib/siteContent";
import type { ServiceItem } from "../types";
import { adminFieldClass, adminLabelClass } from "./adminFields";

const iconLabels: Record<ServiceItem["icon"], string> = {
  stethoscope: "Diagnóstico",
  cpu: "Hardware",
  sparkles: "Manutenção",
  shield: "Segurança",
  hardDrive: "Dados",
  wifi: "Redes",
};

export function AdminServicos() {
  const { content, save, loading } = useSiteContent();
  const [items, setItems] = useState<ServiceItem[]>(content.services.items);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!loading) setItems(content.services.items);
  }, [content.services.items, loading]);

  if (loading) {
    return <p className="text-muted">A carregar…</p>;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next: SiteContent = {
      ...content,
      services: {
        heading: String(form.get("heading") ?? ""),
        subtitle: String(form.get("subtitle") ?? ""),
        ctaTitle: String(form.get("ctaTitle") ?? ""),
        ctaText: String(form.get("ctaText") ?? ""),
        items: items.map((item, index) => ({
          ...item,
          title: String(form.get(`title-${index}`) ?? item.title),
          description: String(form.get(`description-${index}`) ?? item.description),
          duration: String(form.get(`duration-${index}`) ?? item.duration),
          icon: String(form.get(`icon-${index}`) ?? item.icon) as ServiceItem["icon"],
        })),
      },
    };
    setSaving(true);
    const error = await save(next);
    setSaving(false);
    setOk(!error);
    setMessage(error ?? "Guardado. O site público já usa este texto.");
    if (!error) setItems(next.services.items);
  };

  return (
    <form onSubmit={onSubmit} className="max-w-3xl space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-primary">Serviços</h1>
        <p className="mt-1 text-sm text-muted">Títulos, descrições e tempo médio de cada serviço.</p>
      </div>
      <label className={adminLabelClass}>
        Título da secção
        <input name="heading" className={adminFieldClass} defaultValue={content.services.heading} />
      </label>
      <label className={adminLabelClass}>
        Subtítulo
        <input name="subtitle" className={adminFieldClass} defaultValue={content.services.subtitle} />
      </label>

      <div className="space-y-4">
        {items.map((item, index) => (
          <fieldset key={item.id} className="rounded-xl border bg-surface p-4">
            <legend className="px-1 text-sm text-muted">Serviço {index + 1}</legend>
            <label className={adminLabelClass}>
              Título
              <input name={`title-${index}`} className={adminFieldClass} defaultValue={item.title} />
            </label>
            <label className={`${adminLabelClass} mt-3`}>
              Descrição
              <textarea
                name={`description-${index}`}
                rows={3}
                className={adminFieldClass}
                defaultValue={item.description}
              />
            </label>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className={adminLabelClass}>
                Tempo médio
                <input name={`duration-${index}`} className={adminFieldClass} defaultValue={item.duration} />
              </label>
              <label className={adminLabelClass}>
                Ícone
                <select name={`icon-${index}`} className={adminFieldClass} defaultValue={item.icon}>
                  {SERVICE_ICONS.map((icon) => (
                    <option key={icon} value={icon}>
                      {iconLabels[icon]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {items.length > 1 && (
              <button
                type="button"
                className="mt-3 text-sm text-urgent hover:underline"
                onClick={() => setItems(items.filter((_, i) => i !== index))}
              >
                Remover serviço
              </button>
            )}
          </fieldset>
        ))}
      </div>

      <Button
        type="button"
        variant="secondary"
        onClick={() =>
          setItems([
            ...items,
            {
              id: `servico-${Date.now()}`,
              title: "Novo serviço",
              description: "",
              duration: "24h",
              icon: "cpu",
            },
          ])
        }
      >
        Adicionar serviço
      </Button>

      <label className={adminLabelClass}>
        Título do convite no fundo
        <input name="ctaTitle" className={adminFieldClass} defaultValue={content.services.ctaTitle} />
      </label>
      <label className={adminLabelClass}>
        Texto do convite
        <input name="ctaText" className={adminFieldClass} defaultValue={content.services.ctaText} />
      </label>

      {message && (
        <p className={ok ? "text-sm text-accent-terminal" : "text-sm text-urgent"}>{message}</p>
      )}
      <Button type="submit" disabled={saving}>
        {saving ? "A guardar…" : "Guardar"}
      </Button>
    </form>
  );
}
